/* eslint-disable @typescript-eslint/no-magic-numbers */
import React from 'react';
import { Breadcrumbs } from '@cp/ds/src/components/breadcrumbs';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { Container } from '@cp/ds/src/components/container';
import capitalize from 'lodash/capitalize';
import { Helmet } from 'react-helmet';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { usePathGenerator } from '@cp/entities/clientWebRoutes';
import { Box } from '@cp/ds/src/components/box';
import { useCareerDeclination } from '@cp/entities/career';
import { formatCatalogItemDate } from '@cp/entities/catalogItem';
import { getResumeGeoPoint } from '@cp/entities/resume';
import { useTheme } from '@cp/ds/src/hooks';
import { Typography } from '@cp/ds/src/components/typography';
import { Declination } from '@cp/shared/declination';
import { useTitle as useVacancyTitle } from '@cp/entities/vacancy';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import useMediaQuery from '@mui/material/useMediaQuery';
import { VacancyView } from 'src/entities/vacancy';
import { RoutedLink } from 'src/shared/ui/link';
import {
  CATALOG_DESKTOP_COLUMN_SPACING,
  CatalogListSimilar,
  VacancyCardWidget,
  calcMobileItemListStyleParams,
  useCatalogBreadcrumbs,
} from 'src/widgets/catalog';
import { CatalogItemMap } from 'src/entities/catalog';
import { useAddress } from 'src/entities/vacancy/model';
import { CatalogItemPageViewProps } from './model';
import { CONTENT_MAX_WIDTH_DESKTOP } from './const';
import { Header } from './components/header';
import { BottomBar } from './components/bottomBar';

const View: React.FC<CatalogItemPageViewProps> = ({ pageProps: { catalogType, careerId }, id, data, isLoading }) => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const isLoggedIn = useIsLoggedIn();
  const title = useVacancyTitle(data);
  const address = useAddress(data, { streetOnly: true });
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const generatePath = usePathGenerator();
  const careerDeclination = useCareerDeclination();

  const catalogListParams = React.useMemo(() => {
    const mobileColumnSpacing = calcMobileItemListStyleParams(theme).columnSpacing;
    const itemSpacing = isMobile ? `${mobileColumnSpacing}px` : CATALOG_DESKTOP_COLUMN_SPACING,
      itemWidth = '100%';
    return { itemSpacing, itemWidth };
  }, [theme, isMobile]);

  const metaTags = React.useMemo(() => {
    const _title = capitalize(title).replace(/\.+$/g, '');
    const description = `Вакансия ${careerDeclination(
      1,
      careerId,
      Declination.Genitive,
      // eslint-disable-next-line max-len
    )} от нанимателя. ${_title}. ${address as string}. Найдите работу на youhelp.ru: зарегистрируйтесь, заполните анкету и наниматель позвонит Вам.`;

    if (!data) {
      return {
        title: `Вакансия (#${id})`,
        description,
      };
    }

    const createdAt = new Date(data.createdAt);
    return {
      title: `${_title}. Вакансия от ${formatCatalogItemDate(createdAt)} на YouHelp.ru`,
      description,
    };
  }, [address, careerDeclination, careerId, data, id, title]);

  const baseBreadcrumbItems = useCatalogBreadcrumbs();
  const breadcrumbs = React.useMemo(() => {
    return [
      ...baseBreadcrumbItems,
      {
        path: generatePath({
          page: ClientWebPage.CatalogItem,
          pathParams: { id },
          pageProps: { careerId, catalogType },
        }),
        label: title,
      },
    ];
  }, [baseBreadcrumbItems, careerId, generatePath, id, title, catalogType]);

  const content = (
    <>
      <Breadcrumbs LinkComponent={RoutedLink} items={breadcrumbs} sx={{ mb: 3 }} />
      <VacancyView
        data={data}
        disableInfo={!isLoggedIn}
        isLoading={isLoading}
        map={data && <CatalogItemMap location={getResumeGeoPoint(data)} />}
      />
    </>
  );

  return (
    <>
      <Helmet>
        <title>{metaTags.title}</title>
        <meta content={metaTags.description} name="description" />
      </Helmet>

      <Header careerId={data?.careerId} catalogType={catalogType} id={id} />

      <Box component="main" sx={{ backgroundColor: 'background.paper', pb: isMobile ? 14 : 10 }}>
        <Container maxWidth={isMobile ? undefined : CONTENT_MAX_WIDTH_DESKTOP} sx={{ pt: 2.5 }}>
          {content}
        </Container>
        {data && (
          <Box sx={{ backgroundColor: theme.palette.background.default, py: 2, mt: 2 }}>
            <Container sx={{ containerType: 'inline-size' }}>
              <Typography component="h3" sx={{ mb: 2 }} variant="h3">
                Похожие вакансии
              </Typography>
              <CatalogListSimilar
                CatalogItem={VacancyCardWidget}
                request={{
                  type: data.type,
                  cityId: data.city?.id as number,
                  careerId: data.careerId,
                  pagination: 6,
                }}
                {...catalogListParams}
              />
            </Container>
          </Box>
        )}

        {isSmallScreen && data && <BottomBar catalogType={catalogType} id={id} />}
      </Box>
    </>
  );
};

export default View;
